import {environment} from "../../../../apps/frontend/src/environments/environment"
import {Injectable} from '@angular/core'
import {map, Observable, of} from 'rxjs'
import {HttpClient} from '@angular/common/http'
import {DayPilot} from '@daypilot/daypilot-lite-angular'
import {ConfirmRideModel, DriverModel, WorkdayDriverModel} from "../../../domain/src/lib/driver.model"
import {
  CustomerRideStatistics,
  EventModel,
  EventRevoke,
  OpenRideModel,
  RideDetailModel,
  RideRequestModel,
  SplitRideModel,
  VehicleRideStatistics,
  WorkDayDriver,
} from "../../../domain/src/lib/ride.model"
import {Invoice} from "../../../domain/src/lib/invoice.model"
import {AppLogger, LoggerService} from "./logger.service"
import CalEvent = DayPilot.EventData


@Injectable({
  providedIn: 'root',
})
export class RideService {
  private readonly logger: AppLogger


  constructor(
      private readonly http: HttpClient,
      private readonly loggerService: LoggerService,
  ) {
    this.logger = this.loggerService.create('RideService')
  }


  getLastRidesByEventIds(eventIds: number[]): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/events/${eventIds}/lastrides`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRidesByEventId(eventId: number): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/events/${eventId}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getOpenDriverRidesByYearAndMonth(year: number, month: number, driverUserId?: number): Observable<OpenRideModel[]> {
    let url = `${environment.apiUrl}/rides/open?year=${year}&month=${month}`
    if (driverUserId) url = `${url}&driverId=${driverUserId}`
    return this.http.get<OpenRideModel[]>(url)
  }


  getSplitRidesByYearMonth(year: number, month: number): Observable<SplitRideModel[]> {
    const url = `${environment.apiUrl}/rides/split?year=${year}&month=${month}`
    return this.http.get<SplitRideModel[]>(url)
  }


  getRidesByRideId(rideId: number): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/${rideId}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRideCountByEventId(eventId: number): Observable<number> {
    const url = `${environment.apiUrl}/rides/events/${eventId}/count`
    return this.http.get<number>(url)
  }


  getDriverRidesFromDate(driverId: number, startDate: string, days: number = 1, pending?: boolean): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/drivers/${driverId}?startDate=${startDate}&days=${days}&valid=true&pending=true`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRidesFromDateByDriverId(driverId: number, startDate: string, days: number = 1): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/date/${startDate},${days}/driver/${driverId}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRidesFromDateRangeByDriverId(driverId: number, startDate: string, endDate: string): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/drivers/${driverId}/dates/${startDate},${endDate}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getLastRidesFromDateRangeByDriverId(driverId: number, fromDate: string, endDate: string): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/drivers/${driverId}/dates/${fromDate},${endDate}/lastrides`
    return this.http.get<RideDetailModel[]>(url)
  }


  getLastRidesFromDateRangeByCustomerId(customerId: number, startDate: string, endDate: string): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/customers/${customerId}/dates/${startDate},${endDate}/lastrides`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRidesFromDateTimeByDriverId(driverId: number, startDate: string, days: number = 1): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/datetime/${startDate},${days}/driver/${driverId}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRideCountFromDay(startDate: string, days: number = 1): Observable<number> {
    const url = `${environment.apiUrl}/rides/count?startDate=${startDate}&days=${days}&valid=true`
    return this.http.get<number>(url)
  }


  getPendingRidesCountByVehicleId(vehicleId: number): Observable<number> {
    const url = `${environment.apiUrl}/rides/vehicles/${vehicleId}/pending-rides-count`
    return this.http.get<number>(url)
  }


  getWorkdayDrivers(workDate: string, filterUserId?: number): Observable<WorkdayDriverModel[]> {
    const url = `${environment.apiUrl}/drivers/workdays?workDate=${workDate}&filterUserId=${filterUserId}`
    return this.http.get<WorkdayDriverModel[]>(url)
  }


  getDriversAndVehiclesFromDay(startDate: string, days: number = 1): Observable<WorkDayDriver[]> {
    // const url = `${environment.apiUrl}/rides/date/${startDate},${days}/vehicles`
    const url = `${environment.apiUrl}/rides/vehicles?startDate=${startDate}&days=${days}`
    return this.http.get<WorkDayDriver[]>(url)
  }


  getRides(startDate: string, endDate: string, validated?: boolean, cancelled?: boolean, customerId?: number, vehicleId?: number): Observable<RideDetailModel[]> {
    let url = `${environment.apiUrl}/rides?startDate=${startDate}&endDate=${endDate}&valid=${validated}&cancelled=${cancelled}`
    if (vehicleId) url += `&vehicle=${vehicleId}`
    if (customerId) url += `&customer=${customerId}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getRidesFromDay(startDate: string, days: number = 1): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides?startDate=${startDate}&days=${days}&valid=true&cancelled=false`
    return this.http.get<RideDetailModel[]>(url)
  }


  getVehicleRides(vehicleId: number, startDate: string, endDate: string): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/vehicles/${vehicleId}?startDate=${startDate}&endDate=${endDate}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getCustomerRides(customerId: number, startDate: string, endDate: string): Observable<RideDetailModel[]> {
    const url = `${environment.apiUrl}/rides/customers/${customerId}?startDate=${startDate}&endDate=${endDate}`
    return this.http.get<RideDetailModel[]>(url)
  }


  getCalEvents(startDate: string, days: number = 1, onlyValid: boolean = true): Observable<CalEvent[]> {
    const url = `${environment.apiUrl}/rides/calendar/events?startDate=${startDate}&days=${days}&valid=${onlyValid}`
    return this.http.get<CalEvent[]>(url)
  }


  getCalEventsById(id: number): Observable<CalEvent[]> {
    const url = `${environment.apiUrl}/rides/calendar/events/${id}`
    return this.http.get<CalEvent[]>(url)
  }


  requiresTixiWheelchair(rideId: number): Observable<boolean> {
    const url = `${environment.apiUrl}/rides/${rideId}/tixi-wheelchair`
    return this.http.get<boolean>(url)
  }


  hasHandicap(rideId: number, handicap: string): Observable<boolean> {
    const url = `${environment.apiUrl}/rides/${rideId}/handicaps/${handicap}`
    return this.http.get<boolean>(url)
  }


  getInvoicesByYearAndMonth(year: number, month: number): Observable<Invoice[]> {
    const url = `${environment.apiUrl}/rides/invoices?year=${year}&month=${month}`
    return this.http.get<Invoice[]>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  fixInvoiceByEventIds(eventIds: number[]): Observable<number> {
    const url = `${environment.apiUrl}/rides/invoices/${eventIds}/fix`
    return this.http.patch<number>(url, null).pipe(
        map(value => {
          return value
        }),
    )
  }


  getVehicleRideStatisticsByYear(year: number): Observable<VehicleRideStatistics[]> {
    const url = `${environment.apiUrl}/reports/rides/statistics/vehicles?year=${year}`
    return this.http.get<VehicleRideStatistics[]>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  getCustomerRideStatisticsByYear(year: number): Observable<CustomerRideStatistics[]> {
    const url = `${environment.apiUrl}/reports/rides/statistics/customers?year=${year}`
    return this.http.get<CustomerRideStatistics[]>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  confirmExecutedRide(rideId: number, driverId: number): Observable<any> {
    const url = `${environment.apiUrl}/rides/confirm`
    const confirmRequest: ConfirmRideModel = {rideId, driverId}
    return this.http.put(url, confirmRequest).pipe(
        map(value => {
          return value
        }),
    )
  }


  locateDriversDistance(rideId: number): Observable<DriverModel[]> {
    const url = `${environment.apiUrl}/rides/${rideId}/drivers/distance`
    return this.http.get<DriverModel[]>(url)
  }


  revokeRidesByEventId(eventId: number): Observable<number> {
    const url = `${environment.apiUrl}/rides/events/${eventId}/revoke`
    const request = <EventRevoke>{eventId: eventId}
    return this.http.patch<number>(url, request).pipe(
        map(value => {
          return value
        }),
    )
  }


  revokeRideByRideId(rideId: number): Observable<number> {
    const url = `${environment.apiUrl}/rides/${rideId}/revoke`
    const request = <EventRevoke>{rideId: rideId}
    return this.http.patch<number>(url, request).pipe(
        map(value => {
          return value
        }),
    )
  }


  updateRide(ride: RideDetailModel, doEditSerialRide: boolean): Observable<any> {
    const kind = doEditSerialRide ? 'serial' : 'single'
    const url = `${environment.apiUrl}/rides/${kind}`
    const rideRequest = this.convertToRideRequest(ride)
    return this.http.put(url, rideRequest).pipe(
        map(value => {
          return value
        }),
    )
  }


  private convertToRideRequest(ride: RideDetailModel): RideRequestModel {
    return <RideRequestModel>{
      eventId: ride.eventId,
      rideId: ride.rideId,
      isVehicleBlocked: ride.isVehicleBlocked,
      isEventValidated: ride.isEventValidated,
      isRideValidated: ride.isRideValidated,
      flatCost: ride.flatCost,
      isForeignRide: ride.isForeignRide,
      doSendInvoice: ride.doSendInvoice,
      isFreeOfCharge: ride.isFreeOfCharge,
      additionalWaitTime: ride.prevRideWaitTime,
      customerId: ride.customerId,
      handicaps: ride.handicaps,
      departureAddressId: ride.departureAddressId,
      arrivalAddressId: ride.arrivalAddressId,
      vehicleId: ride.vehicleId,
      prevRideId: ride.prevRideId,
      additionalPassengers: ride.additionalPassengers,
      escorts: ride.escorts,
      prevRideWaitTime: ride.prevRideWaitTime,
      createdById: ride.createdById,
      appointmentAt: ride.appointmentAt,
      departureAt: ride.departureAt,
      arrivalAt: ride.arrivalAt,
      distance: ride.distance,
      duration: ride.duration,
      periodEventId: ride.periodEventId,
      periodId: ride.periodId,
      periodStartAt: ride.periodStartAt,
      periodFinishOn: ride.periodFinishOn,
      // periodExcludedDates: ride.periodExcludedDates,
      // periodIncludedDates: ride.periodIncludedDates,
      rideComments: ride.rideComments,
      rideInternalComment: ride.rideInternalComment,
      eventComments: ride.eventComments,
      payingMethodId: ride.payingMethodId,
      operationUserId: ride.operationUserId,
      isDistanceFixed: ride.isDistanceFixed,
    }
  }


  cancelRideByRideId(rideId: number, userId: number, comments?: string): Observable<any> {
    const url = `${environment.apiUrl}/rides/${rideId}/cancel`
    return this.http.patch(url, {userId: userId, comments: comments}).pipe(
        map(value => {
          return value
        }),
    )
  }


  handOverRideByRideId(rideId: number, workDayDriver: WorkDayDriver): Observable<any> {
    const url = `${environment.apiUrl}/rides/${rideId}/handover`
    return this.http.patch(url, workDayDriver).pipe(
        map(value => {
          return value
        }),
    )
  }


  addRidesAndGetEvent(rides: RideDetailModel[]): Observable<any[]> {
    const url = `${environment.apiUrl}/rides`
    return this.http.post<any[]>(url, rides).pipe(
        map(value => {
          return value
        }),
    )
  }


  private handleError(error: any): Promise<any> {
    return Promise.reject(error.message || error)
  }


  replacePassengerCount(rideId: number, passengerCount: number): Observable<any> {
    const url = `${environment.apiUrl}/rides/${rideId}/passengers`
    return this.http.put(url, passengerCount).pipe(
        map(value => {
          return value
        }),
    )
  }


  /*
   addVRide(ride: VRideModel): Observable<any> {
   const url = `${environment.apiUrl}/rides`
   // if (ride.appointmentAt) {
   //   const appointmentAt = Date.parse(ride.appointmentAt)
   // }
   return this.http.post<number>(url, ride).pipe(
   map(value => {
   return value
   })
   )
   }
   */

  deleteRidesByRideIds(RideIds: number[]) {
    const url = `${environment.apiUrl}/rides/${RideIds}`
    return this.http.delete<number>(url).pipe(
        map(value => {
          return value
        }),
        // catchError(this.handleError('deleteRides')),
    )
  }


  deleteRidesByEventIds(ids: number[]) {
    const url = `${environment.apiUrl}/rides/events/${ids}`
    return this.http.delete<number>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  cancelRidesByEventIds(eventIds: number[]) {
    const url = `${environment.apiUrl}/rides/events/${eventIds}`
    return this.http.patch(url, {valid: false}).pipe(
        map(value => {
          return value
        }),
    )
  }


  deleteSerialRidesByRideIds(rideIds: number[]) {
    const url = `${environment.apiUrl}/rides/${rideIds}/serials`
    return this.http.delete<number>(url).pipe(
        map(value => {
          return value
        }),
        // catchError(this.handleError('deleteUser')),
    )
  }


  deleteSerialRidesByPeriodEventId(
      periodEventId: number | null | undefined
      , includeAllEventRides: boolean
      , startDate?: string | null
      , departureAddressId?: number | null | undefined
      , endDate?: string | null) {
    if (!periodEventId) {
      this.logger.warn('deleteSerialRidesByPeriodEventId', 'No periodEventId provided, aborting')
      return
    }
    let url = `${environment.apiUrl}/rides/serials/${periodEventId}?includeAllEventRides=${includeAllEventRides}&startDate=${startDate}&departureAddressId=${departureAddressId}&endDate=${endDate}`
    // if (includeAllEventRides) {
    //   url += `&includeAllEventRides=${includeAllEventRides}`
    // }
    return this.http.delete<number>(url)
  }


  deleteSerialRidesByEventId(eventId: number) {
    const url = `${environment.apiUrl}/rides/events/${eventId}/serials`
    return this.http.delete<number>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  getEventById(id: number): Observable<EventModel> {
    const url = `${environment.apiUrl}/events/${id}`
    return this.http.get<EventModel>(url)
  }
}

/**
 * Handle Http operation that failed.
 * Let the app continue.
 *
 * @param operation - name of the operation that failed
 * @param result - optional value to return as the observable result
 */
/*
 function handleError<T>(operation = 'operation', result?: T) {
 return (error: any): Observable<T> => {

 // TODO: send the error to remote logging infrastructure
 console.error(error) // log to console instead

 // TODO: better job of transforming error for user consumption
 this.logger.error(`${operation} failed: ${error.message}`)

 // Let the app keep running by returning an empty result.
 return of(result as T)
 }
 }
 */

/*
 // Option 1: Convert to arrow function (if it's a class method)
 private handleError<T>(operation = 'operation', result ? : T)
 {
 return (error: any): Observable<T> => {
 console.error(error)
 this.logger.error(`${operation} failed: ${error.message}`)
 return of(result as T)
 }
 }
 */


// Option 2: If it must be a standalone function, pass logger as parameter
function handleError<T>(logger: AppLogger, operation = 'operation', result?: T) {
  return (error: any): Observable<T> => {
    console.error(error)
    logger.error(`${operation} failed: ${error.message}`)
    return of(result as T)
  }
}

